import React, { useEffect, useState } from 'react'
import { Button, Col, Modal, Row, Select, Spin, Tag, Typography } from 'antd'
import {
  PlusCircleOutlined,
  EyeOutlined,
  StopOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getServicePrincipals,
  ServicePrincipalActions,
} from '~/Store/ServicePrincipal'
import {
  ApiKeyActions,
  getApiKeys,
  getIsLoadingApiKeys,
  getIsRevokingApiKey,
  getListServicePrincipalId,
  getListStatusFilter,
} from '~/Store/ApiKey'
import { ScrollBar } from '~/Components/ScrollBar'
import { NoData } from '~/Components/NoData'
import { Utils } from '~/Utils'
import { ApiKeyStatus, IApiKey } from '~/Services/ApiKey.interface'
import { ApiKeyClaimsModal } from './ApiKeyClaimsModal'

const { confirm } = Modal
const { Option } = Select

const statusTag = (status: ApiKeyStatus, t: (key: string) => string) => {
  switch (status) {
    case ApiKeyStatus.Active:
      return <Tag color="green">{t('admin.akStatusActive')}</Tag>
    case ApiKeyStatus.Revoked:
      return <Tag color="red">{t('admin.akStatusRevoked')}</Tag>
    case ApiKeyStatus.Expired:
      return <Tag color="orange">{t('admin.akStatusExpired')}</Tag>
    default:
      return <Tag>{t('admin.akStatusInactive')}</Tag>
  }
}

export const ApiKeys = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const servicePrincipals = useAppSelector(getServicePrincipals)
  const apiKeys = useAppSelector(getApiKeys)
  const isLoading = useAppSelector(getIsLoadingApiKeys)
  const isRevoking = useAppSelector(getIsRevokingApiKey)
  // Kept in Redux (not component-local state) so they survive navigating to
  // New/Edit and back via history.goBack() — the list page otherwise remounts.
  const servicePrincipalId = useAppSelector(getListServicePrincipalId)
  const status = useAppSelector(getListStatusFilter) as ApiKeyStatus | undefined

  const [claimsModalVisible, setClaimsModalVisible] = useState(false)

  const setServicePrincipalId = (id?: string) =>
    dispatch(ApiKeyActions.setListServicePrincipalId(id))
  const setStatus = (value?: ApiKeyStatus) =>
    dispatch(ApiKeyActions.setListStatusFilter(value))

  useEffect(() => {
    dispatch(ServicePrincipalActions.getServicePrincipals({ paging: false }))
  }, [])

  useEffect(() => {
    if (servicePrincipalId) {
      dispatch(ApiKeyActions.getApiKeys({ servicePrincipalId, status }))
    }
  }, [servicePrincipalId, status])

  const newApiKeyClick = () => {
    history.push('/Organizations/ApiKeys/New', { servicePrincipalId })
  }

  const editClick = (item: IApiKey) => {
    history.push('/Organizations/ApiKeys/Edit', { apiKey: item })
  }

  const viewPermissionsClick = (item: IApiKey) => {
    dispatch(ApiKeyActions.getApiKeyClaims(item.id))
    setClaimsModalVisible(true)
  }

  const closeClaimsModal = () => {
    setClaimsModalVisible(false)
    dispatch(ApiKeyActions.resetViewClaims())
  }

  const revokeClick = (item: IApiKey) => {
    confirm({
      title: t('admin.akConfirmRevoke'),
      icon: <ExclamationCircleFilled />,
      content: <div>{item.name || item.keyPrefix}</div>,
      okText: t('admin.akRevoke'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        if (!servicePrincipalId) return
        dispatch(
          ApiKeyActions.revokeApiKey({ servicePrincipalId, apiKeyId: item.id }),
        )
      },
    })
  }

  return (
    <div>
      <ApiKeyClaimsModal
        visible={claimsModalVisible}
        onClose={closeClaimsModal}
      />
      <Row align="middle" style={{ marginBottom: '16px' }}>
        <Typography.Title style={{ flex: 1, margin: 0 }} level={4}>
          {t('admin.akManageKeys')}
        </Typography.Title>
        <Select
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder={t('admin.akSelectServicePrincipal')}
          style={{ width: 240, marginRight: '10px' }}
          value={servicePrincipalId}
          onChange={setServicePrincipalId}
        >
          {servicePrincipals.map(sp => (
            <Option key={sp.id} value={sp.id}>
              {sp.name}
            </Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder={t('admin.spFilterStatus')}
          style={{ width: 160, marginRight: '10px' }}
          value={status}
          onChange={setStatus}
        >
          <Option value={ApiKeyStatus.Active}>{t('admin.akStatusActive')}</Option>
          <Option value={ApiKeyStatus.Revoked}>{t('admin.akStatusRevoked')}</Option>
          <Option value={ApiKeyStatus.Expired}>{t('admin.akStatusExpired')}</Option>
          <Option value={ApiKeyStatus.Inactive}>{t('admin.akStatusInactive')}</Option>
        </Select>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={newApiKeyClick}
        >
          {t('admin.akNewApiKey')}
        </Button>
      </Row>

      {!servicePrincipalId && (
        <Typography.Paragraph type="secondary" style={{ marginTop: '20px' }}>
          {t('admin.akSelectSpFirst')}
        </Typography.Paragraph>
      )}

      {servicePrincipalId && (
        <>
          <div className="listItemBordered" style={{ fontWeight: 'bold' }}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={6} md={6}>
                {t('admin.name')}
              </Col>
              <Col xs={24} sm={4} md={4}>
                {t('admin.akKeyPrefix')}
              </Col>
              <Col xs={24} sm={3} md={3}>
                {t('admin.status')}
              </Col>
              <Col xs={24} sm={4} md={4}>
                {t('admin.akExpiry')}
              </Col>
              <Col xs={24} sm={4} md={4}>
                {t('admin.createdAt')}
              </Col>
              <Col xs={24} sm={3} md={3} />
            </Row>
          </div>
          <Spin spinning={isLoading || isRevoking}>
            <ScrollBar
              style={{
                paddingRight: '16px',
                marginRight: '-16px',
                height: 'calc(100vh - 330px)',
              }}
            >
              {apiKeys.length > 0 ? (
                apiKeys.map(key => (
                  <div
                    className="listItemBordered"
                    key={key.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => editClick(key)}
                  >
                    <Row gutter={[8, 8]} align="middle">
                      <Col xs={24} sm={6} md={6}>
                        {key.name || '-'}
                      </Col>
                      <Col
                        xs={24}
                        sm={4}
                        md={4}
                        style={{ fontFamily: 'monospace', color: '#999' }}
                      >
                        {key.keyPrefix}
                      </Col>
                      <Col xs={24} sm={3} md={3}>
                        {statusTag(key.status, t)}
                      </Col>
                      <Col xs={24} sm={4} md={4}>
                        {key.expiresAt
                          ? new Date(key.expiresAt).toLocaleDateString()
                          : t('admin.akNoExpiryLabel')}
                      </Col>
                      <Col xs={24} sm={4} md={4}>
                        {Utils.toLocaleDateTimeString(key.createdAt)}
                      </Col>
                      <Col xs={24} sm={3} md={3} style={{ textAlign: 'end' }}>
                        <EyeOutlined
                          style={{ color: '#1890ff', marginRight: '20px' }}
                          onClick={evt => {
                            evt.stopPropagation()
                            viewPermissionsClick(key)
                          }}
                        />
                        {key.status !== ApiKeyStatus.Revoked && (
                          <StopOutlined
                            style={{ color: 'red' }}
                            onClick={evt => {
                              evt.stopPropagation()
                              revokeClick(key)
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <NoData content={t('admin.noData')} />
              )}
            </ScrollBar>
          </Spin>
        </>
      )}
    </div>
  )
}
